import React, { useCallback, useMemo } from 'react';

import fireIcon from 'assets/icons/fireButtonIcon.json';
import Lottie from 'lottie-react';
import dayjs from 'dayjs';
import classNames from 'classnames';
import { blockchainUnit } from 'lib/blockChain';

interface IInfoProps {
  name: string | undefined;
  isRecommend: boolean;
  mintDate: number | undefined;
  mintTime: string;
  supplyCount: number | undefined;
  whitelistPrice: number | undefined | string;
  publicPrice: number | undefined | string;
  whitelistCount: number | undefined;
  raffleCount: number | undefined;
  blockchain: string;
  isChangeStyle?: boolean;
}

const Info: React.FC<IInfoProps> = ({
  name,
  isRecommend,
  mintDate,
  mintTime,
  supplyCount,
  whitelistPrice,
  publicPrice,
  raffleCount,
  blockchain,
  isChangeStyle,
}) => {
  const timeZoneOffset = dayjs().utcOffset();
  const sign = timeZoneOffset >= 0 ? '+' : '-';
  const hour = Math.floor(Math.abs(timeZoneOffset) / 60);
  const timeZone = `UTC${sign}${hour.toString()}`;

  const mintLocalFormat = useCallback(() => {
    if (!!mintDate && !!mintTime) {
      return (
        <span>
          {dayjs(`${dayjs.utc(mintDate).format('YYYY-MM-DD')}T${mintTime}Z`)
            .utc()
            .local()
            .format('MMMM D, YYYY HH:mm')}
          <span className="">&nbsp;({timeZone})</span>
        </span>
      );
    } else if (!!mintDate && !mintTime) {
      return <span>{dayjs(mintDate).format('MMMM D, YYYY')}</span>;
    } else {
      return null;
    }
  }, [mintDate, mintTime, timeZone]);

  const raiseCount = useMemo(() => {
    if (
      !(
        (!whitelistPrice && Number(whitelistPrice) !== 0) ||
        whitelistPrice === ''
      ) &&
      !((!publicPrice && Number(publicPrice) !== 0) || publicPrice === '') &&
      !(!supplyCount || Number(supplyCount) === 0)
    ) {
      return Math.ceil(
        ((Number(whitelistPrice) + Number(publicPrice)) / 2) *
          Number(supplyCount)
      );
    } else {
      return -1;
    }
  }, [publicPrice, supplyCount, whitelistPrice]);

  const countRender = useCallback((count: number) => {
    if (Number.isInteger(count)) {
      return count;
    } else {
      return count.toFixed(1);
    }
  }, []);

  const mintInfoRender = useCallback(() => {
    if (
      (!supplyCount || Number(supplyCount) === 0) &&
      (!publicPrice || publicPrice === '') &&
      Number(publicPrice) !== 0 &&
      (!whitelistPrice || whitelistPrice === '') &&
      Number(whitelistPrice) !== 0 &&
      (!raffleCount || Number(raffleCount) === 0)
    ) {
      return null;
    } else {
      return (
        <div
          className={classNames(
            'mt-2 flex flex-wrap items-center gap-2  font-normal text-neutral-400',
            {
              'text-xs': isChangeStyle,
              'text-sm': !isChangeStyle,
            }
          )}
        >
          {!!supplyCount && Number(supplyCount) !== 0 && (
            <div className="flex h-7 items-center justify-center gap-1 rounded-[52px] bg-neutral-800 px-2">
              <div>Supply</div>
              <div className="font-medium text-neutral-100">
                {supplyCount >= 10000
                  ? `${countRender(supplyCount / 1000)}K`
                  : supplyCount}
              </div>
            </div>
          )}

          {(!(!whitelistPrice || whitelistPrice === '') ||
            Number(whitelistPrice) === 0) && (
            <div className="flex h-7 items-center justify-center gap-1 rounded-[52px] bg-neutral-800 px-2">
              <div>WL Price</div>
              <div className="font-medium text-neutral-100">
                {Number(whitelistPrice) !== 0
                  ? `${whitelistPrice} ${blockchainUnit(blockchain)}`
                  : 'Free'}
              </div>
            </div>
          )}

          {(!(!publicPrice || publicPrice === '') ||
            Number(publicPrice) === 0) && (
            <div className="flex h-7 items-center justify-center gap-1 rounded-[52px] bg-neutral-800 px-2">
              <div>Public Price</div>
              <div className="font-medium text-neutral-100">
                {Number(publicPrice) !== 0
                  ? `${publicPrice} ${blockchainUnit(blockchain)}`
                  : 'Free'}
              </div>
            </div>
          )}

          {raiseCount !== -1 && (
            <div className="flex h-7 items-center justify-center gap-1 rounded-[52px] bg-neutral-800 px-2">
              <div>Raise</div>
              <div className="font-medium text-neutral-100">
                {raiseCount} {blockchainUnit(blockchain)}
              </div>
            </div>
          )}

          {!!raffleCount && Number(raffleCount) !== 0 && (
            <div className="flex h-7 items-center justify-center gap-1 rounded-[52px] bg-neutral-800 px-2">
              <div className="font-medium text-neutral-100">{raffleCount}</div>
              <div>Raffles</div>
            </div>
          )}
        </div>
      );
    }
  }, [
    blockchain,
    countRender,
    isChangeStyle,
    publicPrice,
    raffleCount,
    raiseCount,
    supplyCount,
    whitelistPrice,
  ]);

  return (
    <div className="w-full text-neutral-100 ">
      <div className=" w-full break-all line-clamp-1">
        <div className=" text-2xl font-semibold">{name}</div>
      </div>
      <div className="mt-1 flex items-center gap-1">
        {isRecommend && (
          <Lottie animationData={fireIcon} style={{ width: 16 }} />
        )}
        <div className="text-xs font-normal">{mintLocalFormat()}</div>
      </div>
      <div>{mintInfoRender()}</div>
    </div>
  );
};

export default Info;
